import request from "@/api/request";
import { PlaydateCamera } from "@/interfaces/Playdates/PlaydateCamera";
import { AvailableTimeslots } from "@/interfaces/Playdates/AvailableTimeslots";
import { PublicPlaydate } from "@/interfaces/Playdates/PublicPlaydate";
import { PrivatePlaydate } from "@/interfaces/Playdates/PrivatePlaydate";
import { PlaydateRequest } from "@/interfaces/Playdates/PlaydateRequest.ts";
import { CameraList } from "@/interfaces/Playdates/CameraList";
import { PublicPlaydateList } from "@/interfaces/Playdates/PublicPlaydateList";
import { PrivatePlaydateList } from "@/interfaces/Playdates/PrivatePlaydateList";
import { Extend } from "@/interfaces/Playdates/Extend";
import { Feedback } from "@/interfaces/Playdates/Feedback";
import { RequestAvailability } from "@/interfaces/Playdates/RequestAvailability";
import { ChatHistory } from "@/interfaces/Playdates/ChatHistory";
import { SendPlaydateFeedbackProps } from "@/interfaces/Playdates/SendPlaydateFeedbackProps.ts";

class PlaydateService {
  static async getProfile(id: number): Promise<PlaydateCamera> {
    return request.get(`/playdates/private/camera/${id}`);
  }

  static async getAvailableTimeslots(id: number, duration: number): Promise<AvailableTimeslots> {
    return request.get(`/playdates/private/request/${id}`, { duration });
  }

  static async getClosestPublicPlaydate(id: number): Promise<PublicPlaydate | null> {
    return request.get(`/playdates/camera/${id}/closest_public_playdate`);
  }

  static async getClosestPrivatePlaydate(id: number): Promise<PrivatePlaydate | null> {
    return request.get(`/playdates/camera/${id}/closest_private_playdate`);
  }

  static async getPendingRequest(id: number): Promise<PlaydateRequest | null> {
    return request.get(`/playdates/camera/${id}/pending_request`);
  }

  static async request(
    id: number,
    props: { duration: number; preferred_date_1: number; preferred_date_2: number; preferred_date_3: number; wishes?: string | null },
  ): Promise<string> {
    return await request.post(`/playdates/private/request/${id}`, props);
  }

  static async getCameras(page: number): Promise<CameraList> {
    return request.get("/playdates/private/cameras", { page });
  }

  static async getPublicPlaydateList(): Promise<PublicPlaydateList> {
    return request.get("/playdates/public/list");
  }

  static async getPrivatePlaydateList(): Promise<PrivatePlaydateList> {
    return request.get("/playdates/private/list");
  }

  static async getPrivatePlaydateExtend(id: number): Promise<Extend> {
    return request.get(`/playdates/private/extend/${id}`);
  }

  static async extend(id: number, duration: number): Promise<string> {
    return await request.post(`/playdates/private/extend/${id}`, {
      duration,
    });
  }

  static async getFeedback(): Promise<Feedback | null> {
    return request.get(`/playdates/feedback`);
  }
  static async sendFeedback(id: number, props: SendPlaydateFeedbackProps): Promise<void> {
    return request.post(`/playdates/feedback/${id}`, props);
  }

  static async closeFeedback(id: number): Promise<void> {
    return request.get(`/playdates/feedback/close/${id}`);
  }

  static async getPlaydate(type: string, id: number): Promise<PublicPlaydate | PrivatePlaydate> {
    return await request.get(`/playdates/${type}/${id}`);
  }

  static async checkPrivatePlaydateStatus(id: number): Promise<string> {
    return request.get(`/playdates/private/status/${id}`);
  }

  static async sendNotification(type: string, id: number): Promise<boolean> {
    return request.get(`/playdates/${type}/notification/${id}`);
  }

  static async checkRequestAvailability(id: number): Promise<RequestAvailability> {
    return request.get(`/playdates/private/request/check/${id}`);
  }

  static async getChatHistory(token: string): Promise<ChatHistory> {
    return request.get(`/playdates/public/chat-history`, { token });
  }
}

export default PlaydateService;
